import React, {
    useMemo,
    forwardRef,
    useImperativeHandle,
    useRef,
    useEffect,
    useState,
} from "react";
import PropTypes from "prop-types";
import { Chart } from "react-chartjs-2";
import MDBox from "components/MDBox";
import CircularProgress from "@mui/material/CircularProgress";

const ChartContainer = forwardRef(({ height, data, options, plugins, type }, ref) => {
    const chartRef = useRef(null);
    const [shouldRenderChart, setShouldRenderChart] = useState(false);

    // ✅ Expose chartInstance + resetRevealAnimation
    useImperativeHandle(ref, () => ({
        getChart: () => chartRef.current,
        resetRevealAnimation: () => {
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetRevealAnimation) {
                chartInstance.resetRevealAnimation();
            }
        },
    }));

    // Validate chart data before rendering
    const isValidChartData = useMemo(() => {
        return (
            data &&
            Array.isArray(data.labels) &&
            data.labels.length > 0 &&
            Array.isArray(data.datasets) &&
            data.datasets.length > 0 &&
            data.datasets.every((ds) => Array.isArray(ds.data) && ds.data.length > 0)
        );
    }, [data]);

    // Delay to show spinner before rendering chart
    useEffect(() => {
        if (isValidChartData) {
            const timer = setTimeout(() => {
                setShouldRenderChart(true);
            }, 300);

            return () => clearTimeout(timer);
        } else {
            setShouldRenderChart(false);
        }
    }, [isValidChartData]);

    return (
        <MDBox
            sx={{
                height: height || "400px",
                width: "100%",
                p: 2,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {!shouldRenderChart && (
                <MDBox
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1,
                    }}
                >
                    <CircularProgress size={40} thickness={4} color="accent" />
                </MDBox>
            )}

            {shouldRenderChart && (
                <Chart
                    ref={chartRef}
                    type={type}
                    data={data}
                    options={options}
                    plugins={plugins}
                />
            )}
        </MDBox>
    );
});

ChartContainer.propTypes = {
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.object.isRequired,
    options: PropTypes.object.isRequired,
    plugins: PropTypes.array,
    type: PropTypes.string,
};

ChartContainer.defaultProps = {
    height: "400px",
    plugins: [],
    type: "bar",
};

export default ChartContainer;
